import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

/**
 * A 12px fade on whichever edge of a sideways scroller has more behind it.
 *
 * A strip of tabs or chips wider than a phone used to stop at the glass
 * with no sign it went on ("Team, Invite people, Access requests, Wo").
 * The fade is the sign. Returns the style to put on the scroller; it
 * follows scrolling and resizing.
 */
export function useEdgeFade(ref: RefObject<HTMLElement | null>, deps: unknown[] = []): CSSProperties | undefined {
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  if (!edges.left && !edges.right) return undefined;
  const mask = `linear-gradient(to right, ${edges.left ? 'transparent, #000 12px' : '#000'}, ${edges.right ? '#000 calc(100% - 12px), transparent' : '#000'})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}
